import type {
    InfiniteData,
    UseInfiniteQueryResult,
    UseMutationResult,
} from '@tanstack/react-query';

import type { AuditLogPageDto } from '@/services/types';

export const AUDIT_OUTCOMES = ['success', 'denied', 'failed'] as const;
export type AuditOutcome = (typeof AUDIT_OUTCOMES)[number];

export const AUDIT_ACTOR_TYPES = ['user', 'api_user', 'production_key', 'anonymous'] as const;
export type AuditActorType = (typeof AUDIT_ACTOR_TYPES)[number];

export const AUDIT_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
export type AuditMethod = (typeof AUDIT_METHODS)[number];

export const AUDIT_IMPERSONATION = ['only', 'exclude'] as const;
export type AuditImpersonation = (typeof AUDIT_IMPERSONATION)[number];

export const AUDIT_SCOPE_KINDS = ['client', 'location', 'cage'] as const;
export type AuditScopeKind = (typeof AUDIT_SCOPE_KINDS)[number];

export const AUDIT_SCOPE_STATES = [
    'active',
    'archived',
    'deleted',
    'historical',
    'missing',
] as const;
export type AuditScopeState = (typeof AUDIT_SCOPE_STATES)[number];

/**
 * A client, site or pen an entry is scoped to, named as it is now rather than as it was
 * when the entry was written. `missing` means the id resolves to no row.
 */
export interface AuditScopeRef {
    id: number;
    /** Null for a missing row, and for a client that has no name. */
    name: string | null;
    state: AuditScopeState;
}

export interface AuditLogEntry {
    id: string;
    occurredAt: Date;
    actorType: string;
    actorUserId: number | null;
    actorEmail: string | null;
    impersonatorUserId: number | null;
    impersonatorEmail: string | null;
    action: string;
    outcome: AuditOutcome;
    statusCode: number | null;
    entityType: string;
    entityId: string | null;
    entityLabel: string | null;
    client: AuditScopeRef | null;
    location: AuditScopeRef | null;
    cage: AuditScopeRef | null;
    requestId: string | null;
    ip: string | null;
    userAgent: string | null;
    method: string | null;
    route: string | null;
    changes: Record<string, unknown> | null;
    metadata: Record<string, unknown> | null;
}

/**
 * Every filter the list and the export take. The keys are the API's query param names,
 * which are also the page's URL keys, so a link reproduces the list and the export.
 */
export interface AuditLogFilters {
    from?: Date;
    /** Exclusive. */
    to?: Date;
    entryId?: string;
    /** Part of the actor or the impersonator email. */
    actorEmail?: string;
    actorType?: AuditActorType[];
    /** Sent only with exactly one actor type: users and API users share ids. */
    actorUserId?: number;
    impersonation?: AuditImpersonation;
    impersonatorUserId?: number;
    impersonatorEmail?: string;
    actions?: string[];
    /** Action prefix, such as "user.". */
    action?: string;
    excludeActions?: string[];
    entityType?: string[];
    /** Sent only with exactly one entity type. */
    entityId?: string;
    /** Part of the label written with the entry. */
    entityLabel?: string;
    clientId?: number;
    locationId?: number;
    cageId?: number;
    outcome?: AuditOutcome[];
    statusCode?: number;
    requestId?: string;
    ip?: string;
    userAgent?: string;
    method?: AuditMethod[];
    /** Route template prefix. */
    route?: string;
    changedField?: string;
    /** Sent only with changedField. */
    changedFrom?: string;
    /** Sent only with changedField. */
    changedTo?: string;
    /** Dotted path into the request data; alone, it means the path exists. */
    metadataPath?: string;
    /** Sent only with metadataPath; also matches an element of an array there. */
    metadataValue?: string;
}

/** Every key of AuditLogFilters, in the order the URL and the panel list them. */
export const AUDIT_LOG_FILTER_KEYS = [
    'from',
    'to',
    'entryId',
    'actorEmail',
    'actorType',
    'actorUserId',
    'impersonation',
    'impersonatorUserId',
    'impersonatorEmail',
    'actions',
    'action',
    'excludeActions',
    'entityType',
    'entityId',
    'entityLabel',
    'clientId',
    'locationId',
    'cageId',
    'outcome',
    'statusCode',
    'requestId',
    'ip',
    'userAgent',
    'method',
    'route',
    'changedField',
    'changedFrom',
    'changedTo',
    'metadataPath',
    'metadataValue',
] as const satisfies readonly (keyof AuditLogFilters)[];

export type AuditLogFilterKey = (typeof AUDIT_LOG_FILTER_KEYS)[number];

/** The actions and entity types recorded so far, for the pickers. */
export interface AuditLogFacets {
    actions: string[];
    entityTypes: string[];
}

/** A client, site or pen found by name for the scope picker, archived and deleted included. */
export interface AuditScopeOption {
    kind: AuditScopeKind;
    id: number;
    name: string | null;
    state: AuditScopeState;
    /** The owning client of a site or pen. */
    clientName: string | null;
    /** The site of a pen. */
    locationName: string | null;
}

// ── Result Types ────────────────────────────────────────────────────────────

export interface UseAuditLogResult
    extends Pick<
        UseInfiniteQueryResult<InfiniteData<AuditLogPageDto>>,
        'fetchNextPage' | 'isFetchingNextPage' | 'error' | 'refetch'
    > {
    /** No page yet: react-query's `isPending`. */
    isLoading: boolean;
    entries: AuditLogEntry[];
    hasNextPage: boolean;
    isRefreshing: boolean;
}

export type UseExportAuditLogResult = UseMutationResult<void, unknown, AuditLogFilters>;

export interface UseAuditFacetsResult {
    facets: AuditLogFacets;
    isLoading: boolean;
    error: unknown;
}

export interface UseAuditScopeSearchResult {
    options: AuditScopeOption[];
    /** True while the debounce waits or a request runs. */
    isFetching: boolean;
    /** False below the minimum query length, when nothing is searched. */
    isSearchable: boolean;
    error: unknown;
}
